import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { CanAccess } from "ra-core";
import { Link, useLocation } from "react-router";

import { GRAVUN_BRAND } from "@/branding";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";

import { VISIBLE_NAV_GROUPS, type NavGroup, type NavItem } from "./navConfig";
import { SidebarUserFooter } from "./SidebarUserFooter";
import { useEntitlements } from "../entitlements/useEntitlements";

/**
 * navConfig groups filtered by the active workspace's plan entitlements.
 * While entitlements load, nothing is hidden (no nav flashing); the server
 * enforces plans regardless of what is visible here.
 */
const useEntitledNavGroups = (): NavGroup[] => {
  const { hasFeature, isPending } = useEntitlements();
  if (isPending) return VISIBLE_NAV_GROUPS;
  return VISIBLE_NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter(
      (item) => !item.feature || hasFeature(item.feature),
    ),
  })).filter((group) => group.items.length > 0);
};

const useIsItemActive = () => {
  const location = useLocation();
  return (item: NavItem) =>
    item.to === "/"
      ? location.pathname === "/"
      : location.pathname === item.to ||
        location.pathname.startsWith(`${item.to}/`);
};

/**
 * Gravun's persistent left navigation, rendered entirely from navConfig.
 * Groups are collapsible parents with chevrons (reference style); the group
 * containing the active route opens automatically. In icon-collapsed mode
 * each group becomes a dropdown so navigation stays one click away.
 */
export const GravunSidebar = () => {
  const isItemActive = useIsItemActive();
  const location = useLocation();
  const navGroups = useEntitledNavGroups();

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    for (const group of VISIBLE_NAV_GROUPS) {
      initial[group.key] = group.items.some((item) =>
        item.to === "/"
          ? location.pathname === "/"
          : location.pathname.startsWith(item.to),
      );
    }
    return initial;
  });

  const toggleGroup = (key: string) =>
    setOpenGroups((open) => ({ ...open, [key]: !open[key] }));

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-14 flex-row items-center justify-between border-b border-sidebar-border px-3 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
        <Link
          to="/"
          className="flex items-center gap-2 no-underline group-data-[collapsible=icon]:hidden"
        >
          <img
            src={GRAVUN_BRAND.wordmarkNavy}
            alt={GRAVUN_BRAND.name}
            className="h-[18px] w-auto shrink-0 dark:hidden"
          />
          <img
            src={GRAVUN_BRAND.wordmarkWhite}
            alt={GRAVUN_BRAND.name}
            className="hidden h-[18px] w-auto shrink-0 dark:block"
          />
        </Link>
        <SidebarTrigger className="size-7 text-muted-foreground" />
      </SidebarHeader>

      <SidebarContent className="gap-0 px-1 py-2">
        <SidebarGroup className="py-0">
          <SidebarGroupContent>
            <SidebarMenu className="gap-0.5">
              {navGroups.map((group) =>
                group.label ? (
                  // The Admin group's items are all permission-gated; without
                  // gating the GROUP too, a sales rep sees an "Admin" header
                  // with nothing under it, a door drawn on a wall.
                  group.key === "admin" ? (
                    <CanAccess
                      key={group.key}
                      resource="configuration"
                      action="list"
                    >
                      <CollapsibleNavGroup
                        group={group}
                        open={!!openGroups[group.key]}
                        onToggle={() => toggleGroup(group.key)}
                      />
                    </CanAccess>
                  ) : (
                    <CollapsibleNavGroup
                      key={group.key}
                      group={group}
                      open={!!openGroups[group.key]}
                      onToggle={() => toggleGroup(group.key)}
                    />
                  )
                ) : (
                  group.items.map((item) => (
                    <TopLevelNavItem
                      key={item.key}
                      item={item}
                      active={isItemActive(item)}
                    />
                  ))
                ),
              )}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="gap-0 border-t border-sidebar-border p-2">
        <SidebarUserFooter />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};

const TopLevelNavItem = ({
  item,
  active,
}: {
  item: NavItem;
  active: boolean;
}) => (
  <SidebarMenuItem>
    <SidebarMenuButton
      asChild
      isActive={active}
      tooltip={item.label}
      className="h-8 gap-2.5 rounded-md px-2 text-[13px] font-medium data-[active=true]:bg-sidebar-accent data-[active=true]:font-semibold data-[active=true]:text-sidebar-accent-foreground"
    >
      <Link to={item.to} className="relative">
        {active ? <ActiveAccent /> : null}
        <item.icon
          className="size-4 shrink-0 text-sidebar-foreground/70"
          strokeWidth={1.75}
        />
        <span>{item.label}</span>
      </Link>
    </SidebarMenuButton>
  </SidebarMenuItem>
);

const CollapsibleNavGroup = ({
  group,
  open,
  onToggle,
}: {
  group: NavGroup;
  open: boolean;
  onToggle: () => void;
}) => {
  const { state, isMobile } = useSidebar();
  const isItemActive = useIsItemActive();
  const groupActive = group.items.some(isItemActive);
  const GroupIcon = group.icon;

  // Icon-collapsed rail: the group becomes a dropdown menu.
  if (state === "collapsed" && !isMobile) {
    return (
      <SidebarMenuItem>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              isActive={groupActive}
              tooltip={group.label}
              className="h-8 rounded-md"
            >
              {GroupIcon ? (
                <GroupIcon
                  className="size-4 text-sidebar-foreground/70"
                  strokeWidth={1.75}
                />
              ) : null}
              <span>{group.label}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-48">
            <DropdownMenuLabel className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {group.label}
            </DropdownMenuLabel>
            {group.items.map((item) => (
              <NavItemGate key={item.key} item={item}>
                <DropdownMenuItem asChild>
                  <Link to={item.to} className="flex items-center gap-2">
                    <item.icon className="size-4 text-muted-foreground" />
                    {item.label}
                  </Link>
                </DropdownMenuItem>
              </NavItemGate>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        onClick={onToggle}
        aria-expanded={open}
        className={cn(
          "h-8 gap-2.5 rounded-md px-2 text-[13px] font-medium",
          groupActive && !open && "text-sidebar-accent-foreground",
        )}
      >
        {GroupIcon ? (
          <GroupIcon
            className="size-4 shrink-0 text-sidebar-foreground/70"
            strokeWidth={1.75}
          />
        ) : null}
        <span>{group.label}</span>
        <ChevronRight
          className={cn(
            "ml-auto size-3.5 text-sidebar-foreground/45 transition-transform duration-150",
            open && "rotate-90",
          )}
        />
      </SidebarMenuButton>
      {open ? (
        <SidebarMenuSub className="mx-0 ml-3.5 animate-in fade-in-0 slide-in-from-top-1 border-sidebar-border px-2 duration-150">
          {group.items.map((item) => {
            const active = isItemActive(item);
            return (
              <NavItemGate key={item.key} item={item}>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    isActive={active}
                    className="h-7 gap-2 rounded-md px-2 text-[13px] data-[active=true]:bg-sidebar-accent data-[active=true]:font-semibold data-[active=true]:text-sidebar-accent-foreground"
                  >
                    <Link to={item.to} className="relative">
                      {active ? <ActiveAccent /> : null}
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </NavItemGate>
            );
          })}
        </SidebarMenuSub>
      ) : null}
    </SidebarMenuItem>
  );
};

/** Wraps an item in CanAccess when it declares a gating resource. */
const NavItemGate = ({
  item,
  children,
}: {
  item: NavItem;
  children: React.ReactNode;
}) =>
  item.resource ? (
    <CanAccess resource={item.resource} action="list">
      {children}
    </CanAccess>
  ) : (
    <>{children}</>
  );

/** Small navy accent bar marking the active row (reference treatment). */
const ActiveAccent = () => (
  <span
    aria-hidden
    className="absolute -left-2 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-full bg-sidebar-primary"
  />
);
